/**
 * The wireframe's read model, derived from what the server actually says.
 *
 * The wireframe talks about a Production Line in seven words — Draft, Needs
 * input, Analyzing, Ready to choose, Generating, Complete, Failed — and about
 * a video in four. The server talks in intake stages, line lifecycle states
 * and creative-run states. This file is the only place that translation
 * lives, and it never infers a state the server did not report.
 */

import type {
  MerchantCreativeRunView,
  MerchantVideoView,
  PendingProductionLineView,
  ProductionLineView,
} from './api.js';
import type { WorkspaceCopy, WorkspaceLanguage } from './copy.js';

export type LineStatusKey =
  | 'draft'
  | 'needs-input'
  | 'analyzing'
  | 'ready-to-choose'
  | 'script-approved'
  | 'generating'
  | 'complete'
  | 'failed'
  | 'active'
  | 'paused'
  | 'archived';

export type LineActionKey =
  | 'brief'
  | 'profile'
  | 'analysis'
  | 'recommendations'
  | 'card'
  | 'progress'
  | 'videos'
  | 'issue'
  | 'resume'
  | 'open';

export interface LineSummary {
  readonly id: string;
  readonly kind: 'pending' | 'line';
  readonly intakeId: string | null;
  readonly title: string;
  readonly websiteUrl: string | null;
  readonly domain: string;
  readonly offer: string;
  readonly prompt: string;
  readonly status: LineStatusKey;
  readonly action: LineActionKey;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly videos: number;
  readonly failReason: string | null;
  readonly href: string;
  readonly actionHref: string;
}

export type VideoStatusKey =
  'generating' | 'ready' | 'needs-review' | 'failed' | 'needs-input' | 'card';

export interface VideoItem {
  readonly id: string;
  readonly run: MerchantCreativeRunView;
  readonly intakeId: string | null;
  readonly lineId: string | null;
  readonly lineTitle: string;
  readonly title: string;
  readonly direction: string;
  readonly status: VideoStatusKey;
  readonly durationSeconds: number | null;
  readonly language: string;
  readonly platform: string;
  readonly aspect: string;
  readonly date: string;
  readonly videoUrl: string | null;
  readonly posterUrl: string | null;
  readonly downloadable: boolean;
  readonly failReason: string | null;
}

/** Working states, in the order the merchant experiences them. */
const RUN_WORKING = new Set([
  'PREPARING',
  'CREATING',
  'CHECKING_QUALITY',
  'QUALITY_REVIEW',
]);

export function videoStatusOf(run: MerchantCreativeRunView): VideoStatusKey {
  if (run.video !== null)
    return run.merchantState === 'QUALITY_REVIEW' ? 'needs-review' : 'ready';
  if (run.merchantState === 'READY') return 'ready';
  if (run.merchantState === 'COULD_NOT_CREATE' || run.merchantState === 'CANCELLED')
    return 'failed';
  if (
    run.merchantState === 'NEEDS_ONE_ITEM' ||
    run.merchantState === 'DIRECTION_NEEDS_REFRESH'
  ) {
    return 'needs-input';
  }
  if (run.merchantState === 'REVIEW_CARD') return 'card';
  return 'generating';
}

export function runIsWorking(run: MerchantCreativeRunView): boolean {
  return RUN_WORKING.has(run.merchantState);
}

export function domainOf(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(
      /^www\./,
      '',
    );
  } catch {
    return url;
  }
}

export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds))
    return null;
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${String(minutes)}:${String(rest).padStart(2, '0')}`;
}

export function formatDate(iso: string, language: WorkspaceLanguage): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-SA' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatTime(iso: string, language: WorkspaceLanguage): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-SA' : 'en', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatRelative(
  iso: string,
  copy: WorkspaceCopy,
  language: WorkspaceLanguage,
  now: number = Date.now(),
): string {
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return '';
  const minutes = Math.round((now - time) / 60_000);
  if (minutes < 1) return copy.common.justNow;
  if (minutes < 60) return copy.common.minutesAgo(minutes);
  const hours = Math.round(minutes / 60);
  if (hours < 24) return copy.common.hoursAgo(hours);
  const days = Math.round(hours / 24);
  if (days === 1) return copy.common.yesterday;
  if (days < 7) return copy.common.daysAgo(days);
  return formatDate(iso, language);
}

export function daysSince(iso: string, now: number = Date.now()): number {
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return Number.POSITIVE_INFINITY;
  return Math.floor((now - time) / 86_400_000);
}

export function videoItemOf(
  run:
    | MerchantVideoView
    | (MerchantCreativeRunView & {
        productionLineId?: string | null;
        productionLineTitle?: string;
        intakeId?: string | null;
      }),
  fallbackLineTitle = '',
): VideoItem {
  const status = videoStatusOf(run);
  const card = run.card;
  const direction = card?.direction ?? '';
  const platform = card?.targetPlatforms.join(' · ') ?? '';
  return {
    id: run.creativeRunId,
    run,
    intakeId: run.intakeId ?? null,
    lineId: run.productionLineId ?? null,
    lineTitle: run.productionLineTitle ?? fallbackLineTitle,
    title: card?.hookLine ?? direction,
    direction,
    status,
    durationSeconds: run.video?.durationSeconds ?? card?.durationSeconds ?? null,
    language: card?.language ?? '',
    platform,
    aspect: card?.aspectRatio ?? '',
    date: run.video?.completedAt ?? run.confirmedAt ?? run.authorizedAt,
    videoUrl: run.video?.videoUrl ?? null,
    posterUrl: run.video?.posterUrl ?? null,
    downloadable: run.video !== null,
    failReason: status === 'failed' || status === 'needs-input' ? run.neededItem : null,
  };
}

/** How the videos on a line decide its status once analysis has finished. */
function statusFromRuns(videos: readonly VideoItem[]): LineStatusKey | null {
  if (videos.length === 0) return null;
  if (videos.some((video) => video.status === 'generating')) return 'generating';
  if (
    videos.some((video) => video.status === 'card' || video.status === 'needs-input')
  ) {
    return 'needs-input';
  }
  if (
    videos.some((video) => video.status === 'ready' || video.status === 'needs-review')
  ) {
    return 'complete';
  }
  return 'failed';
}

function actionForStatus(
  status: LineStatusKey,
  videos: readonly VideoItem[],
): LineActionKey {
  switch (status) {
    case 'draft':
      return 'brief';
    case 'needs-input':
      return videos.some((video) => video.status === 'card') ? 'card' : 'profile';
    case 'analyzing':
      return 'analysis';
    case 'ready-to-choose':
      return 'recommendations';
    case 'generating':
      return 'progress';
    case 'complete':
      return 'videos';
    case 'failed':
      return 'issue';
    case 'paused':
      return 'resume';
    case 'active':
    case 'archived':
    case 'script-approved':
      return 'open';
  }
}

/** Intake stages before analysis completes where Mouja is waiting on the merchant. */
const PENDING_STAGES_NEEDING_PROFILE = new Set([
  'PROFILE_REVIEW',
  'PROFILE_CONFIRMATION',
  'INTAKE',
]);

export function pendingLineSummary(
  line: PendingProductionLineView,
  videos: readonly VideoItem[],
  intakeId: string | null,
): LineSummary {
  const websiteUrl = line.brief?.websiteUrl ?? null;
  const fromRuns = statusFromRuns(videos);
  let status: LineStatusKey;
  if (line.workflowKind === 'SAVED_BRIEF' && line.savedScriptState) {
    // On the saved-script path this is the persisted approval state, not
    // evidence that video generation started or that a video was delivered.
    status =
      line.savedScriptState === 'PLANNING_PRODUCTION'
        ? 'script-approved'
        : ['VALIDATING_HANDOFF', 'PREPARING_CONTENT_CARD'].includes(
              line.savedScriptState,
            )
          ? 'generating'
          : 'needs-input';
  } else if (fromRuns !== null) status = fromRuns;
  else if (line.currentStage === 'COMPLETE') status = 'ready-to-choose';
  else if (line.state === 'NEEDS_ATTENTION') status = 'needs-input';
  else status = 'analyzing';
  // A saved brief already owns an approved profile. A script/research hold is
  // not a request to confirm that profile again (there may be no legacy videos).
  const action =
    line.workflowKind === 'SAVED_BRIEF' && status === 'needs-input'
      ? 'open'
      : actionForStatus(status, videos);
  const href = `/production-lines/${encodeURIComponent(line.productionLineId)}`;
  const failed = videos.find((video) => video.status === 'failed');
  return {
    id: line.productionLineId,
    kind: 'pending',
    intakeId:
      line.workflowKind === 'SAVED_BRIEF'
        ? null
        : (line.intakeId ?? line.brief?.intakeId ?? intakeId),
    title: line.title,
    websiteUrl,
    domain: domainOf(websiteUrl),
    offer: '',
    prompt: line.brief?.contextNote ?? '',
    status,
    action,
    createdAt: line.createdAt,
    updatedAt: latest([
      line.brief?.updatedAt ?? line.createdAt,
      ...videos.map((video) => video.date),
    ]),
    videos: videos.filter(
      (video) => video.status === 'ready' || video.status === 'needs-review',
    ).length,
    failReason: failed?.failReason ?? null,
    href,
    actionHref:
      action === 'videos'
        ? `${href}/videos`
        : action === 'analysis'
          ? `${href}/analysis`
          : href,
  };
}

export function lineSummary(
  line: ProductionLineView,
  videos: readonly VideoItem[],
): LineSummary {
  const state = line.productionLine.state;
  const websiteUrl = line.brief?.websiteUrl ?? null;
  const fromRuns = statusFromRuns(videos);
  const tasteDisabled = line.capabilities?.tasteCalibration.enabled === false;
  let status: LineStatusKey;
  if (state === 'PAUSED') status = 'paused';
  else if (state === 'ARCHIVED') status = 'archived';
  else if (fromRuns !== null) status = fromRuns;
  else if (state === 'DRAFT' || state === 'CALIBRATING') {
    // A configured line always follows a completed analysis; with video
    // Taste Calibration disabled the next decision is the direction.
    status = tasteDisabled ? 'ready-to-choose' : 'draft';
  } else if (state === 'RECOMMENDATION_READY' || state === 'CONFIGURED') {
    status = 'ready-to-choose';
  } else status = 'active';
  const action = actionForStatus(status, videos);
  const href = `/production-lines/${encodeURIComponent(line.productionLine.productionLineId)}`;
  const failed = videos.find((video) => video.status === 'failed');
  return {
    id: line.productionLine.productionLineId,
    kind: 'line',
    intakeId:
      line.brief?.intakeId ??
      videos.find((video) => video.intakeId !== null)?.intakeId ??
      null,
    title: line.productionLine.title,
    websiteUrl,
    domain: domainOf(websiteUrl),
    offer: '',
    prompt: line.brief?.contextNote ?? line.productionLine.standingInstruction ?? '',
    status,
    action,
    createdAt: line.productionLine.createdAt,
    updatedAt: latest([
      line.productionLine.effectiveAt,
      ...videos.map((video) => video.date),
    ]),
    videos: videos.filter(
      (video) => video.status === 'ready' || video.status === 'needs-review',
    ).length,
    failReason: failed?.failReason ?? null,
    href,
    actionHref:
      action === 'videos'
        ? `${href}/videos`
        : action === 'recommendations'
          ? `${href}/recommendations`
          : href,
  };
}

function latest(values: readonly string[]): string {
  return values.reduce(
    (best, value) => {
      const time = new Date(value).getTime();
      return Number.isNaN(time) || time <= new Date(best).getTime() ? best : value;
    },
    values[0] ?? new Date(0).toISOString(),
  );
}

export function lineStatusLabel(status: LineStatusKey, copy: WorkspaceCopy): string {
  return copy.status[status];
}

export function lineActionLabel(action: LineActionKey, copy: WorkspaceCopy): string {
  return copy.action[action];
}

export function videoStatusLabel(status: VideoStatusKey, copy: WorkspaceCopy): string {
  return copy.videoStatus[status];
}

export const ATTENTION_STATUSES: ReadonlySet<LineStatusKey> = new Set([
  'needs-input',
  'ready-to-choose',
  'failed',
]);

export function isPendingStageNeedingProfile(stage: string): boolean {
  return PENDING_STAGES_NEEDING_PROFILE.has(stage);
}
export { sourceObservationText } from './shared/sourceObservation.js';
